/**
 * Ownership claim panel (owner request 2026-10-11): who claims the adventure, where it would go,
 * their statement and proof, and — for site Moderators/Admins while it is open — Approve / Reject.
 *
 * Approving moves the adventure and every management right to the claimant (or the group they
 * named); the original creator keeps the creator credit but can no longer edit it. Unless "Keep
 * current maintainers" is ticked, every maintainer is removed. The current owners never see the
 * claimant's statement or proof; whether they are told the claimant's name is the moderators' call
 * ("Tell the current owner who claimed it", switchable at any time and in both decision dialogs).
 */

import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { CheckCircle, DoNotDisturbOn, Gavel } from '@mui/icons-material'
import { OwnerEntityType, TicketDetailsDto } from '@api/types/tickets'
import { TicketPhotoGrid } from './TicketPhotos'
import { TicketReasonDialog } from './TicketReasonDialog'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import { MAX_CLOSE_REASON_LENGTH, claimRevealDefault } from './ticketActions'

interface OwnershipClaimPanelProps {
  ticket: TicketDetailsDto
  canDecide: boolean
  submitting?: boolean
  /** Approve; revealClaimant is what the dialog's "tell the owner" box was left at. */
  onApprove: (notes: string, keepMaintainers: boolean, revealClaimant: boolean) => void
  onReject: (reason: string, revealClaimant: boolean) => void
  /** Switch whether the current owner is told who claimed it (site staff; any time). */
  onReveal?: (reveal: boolean) => void
}

export function OwnershipClaimPanel({ ticket, canDecide, submitting = false, onApprove, onReject, onReveal }: OwnershipClaimPanelProps) {
  const [dialog, setDialog] = useState<'approve' | 'reject' | null>(null)
  const mobileDialog = useMobileDialogProps()
  const [notes, setNotes] = useState('')
  const [keepMaintainers, setKeepMaintainers] = useState(false)
  const revealed = claimRevealDefault(ticket)
  const [revealChoice, setRevealChoice] = useState(revealed)

  useEffect(() => {
    if (dialog === 'approve') {
      setNotes('')
      setKeepMaintainers(false)
    }
    if (dialog) setRevealChoice(revealed)
  }, [dialog, revealed])

  // Close the dialogs once the decision lands (the ticket is no longer decidable).
  useEffect(() => {
    if (!canDecide) setDialog(null)
  }, [canDecide])

  const redacted = ticket.claimViewerRole === 'Owner'
  const targetKind = ticket.claimToEntityType === OwnerEntityType.Group ? 'Group' : 'User'
  const claimantName = ticket.claimantDisplayName || ticket.createdByDisplayName
  const canReveal = !!onReveal && (ticket.canRevealClaimant ?? ticket.claimViewerRole === 'Moderator')

  const revealBox = (
    <FormControlLabel
      control={<Checkbox checked={revealChoice} onChange={(e) => setRevealChoice(e.target.checked)} disabled={submitting} />}
      label="Tell the current owner who claimed it"
    />
  )

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: 'secondary.main' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Gavel color="secondary" />
        <Typography variant="h6">Ownership claim</Typography>
      </Box>

      {redacted ? (
        revealed && claimantName ? (
          <Alert severity="info">
            Claimed by <strong>{claimantName}</strong>
            {ticket.claimToEntityType === OwnerEntityType.Group && ticket.claimToName
              ? ` for the group ${ticket.claimToName}`
              : ''}
            . Their statement and evidence are only shown to moderators.
          </Alert>
        ) : (
          <Alert severity="info">The claimant and their evidence are only shown to moderators.</Alert>
        )
      ) : (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Claimant
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {claimantName || 'Unknown'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Would go to
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight={500}>
                  {ticket.claimToName || ticket.claimToEntityId || 'The claimant'}
                </Typography>
                <Chip size="small" label={targetKind} sx={{ height: 20, fontSize: '0.7rem' }} />
              </Box>
            </Box>
          </Box>

          <Typography variant="subtitle2" gutterBottom>
            Statement and proof
          </Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {ticket.description || 'No statement.'}
          </Typography>
          <TicketPhotoGrid attachments={ticket.attachments} />
        </>
      )}

      {canReveal && (
        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={revealed}
                onChange={(e) => onReveal?.(e.target.checked)}
                disabled={submitting}
                inputProps={{ 'aria-label': 'Tell the current owner who claimed it' }}
              />
            }
            label="Tell the current owner who claimed it"
          />
          <Typography variant="caption" color="text.secondary" component="div">
            {revealed
              ? 'The owner sees the claimant’s name (and group) in the dispute and its notices. Never the statement or photos.'
              : 'Hidden: the owner only knows the adventure is claimed.'}
          </Typography>
        </Box>
      )}

      {canDecide && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<CheckCircle />}
            onClick={() => setDialog('approve')}
            disabled={submitting}
          >
            Approve claim
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DoNotDisturbOn />}
            onClick={() => setDialog('reject')}
            disabled={submitting}
          >
            Reject claim
          </Button>
        </Box>
      )}

      <Dialog open={dialog === 'approve'} onClose={submitting ? undefined : () => setDialog(null)} fullWidth maxWidth="sm" {...mobileDialog}>
        <DialogTitle>Approve ownership claim</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            The adventure and every management right move to {ticket.claimToName || 'the claimant'}. The original
            creator keeps the creator credit but can no longer edit or manage it. This is logged in the adventure's
            history and both sides are told.
          </DialogContentText>
          <FormControlLabel
            control={
              <Checkbox checked={keepMaintainers} onChange={(e) => setKeepMaintainers(e.target.checked)} disabled={submitting} />
            }
            label="Keep current maintainers"
          />
          <Box sx={{ mb: 1 }}>{revealBox}</Box>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Notes (optional, sent to the claimant)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={submitting}
            inputProps={{ maxLength: MAX_CLOSE_REASON_LENGTH }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => onApprove(notes.trim(), keepMaintainers, revealChoice)}
            disabled={submitting}
          >
            Approve and transfer
          </Button>
        </DialogActions>
      </Dialog>

      <TicketReasonDialog
        open={dialog === 'reject'}
        title="Reject ownership claim"
        description="Nothing changes on the adventure. The claimant is told it was not approved, with this reason; the owner is told the dispute is closed."
        label="Reason"
        confirmLabel="Reject claim"
        confirmColor="error"
        required
        submitting={submitting}
        onCancel={() => setDialog(null)}
        onConfirm={(reason) => onReject(reason, revealChoice)}
      >
        {revealBox}
      </TicketReasonDialog>
    </Paper>
  )
}
